import { Box } from "@mui/material";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  EmptyState,
  Icon,
  Modal,
  SegmentedControl,
  TextField,
  Select,
  DataTable,
} from "@rentiohq/web-shared/dist/components";
import React, { useState, useEffect } from "react";

export type TAmenityDataType = "string" | "number" | "boolean";

export interface IEditableAmenity {
  id?: string;
  name: string;
  dataType: TAmenityDataType;
  value: string | number | boolean;
}

interface IProps {
  isOpen: boolean;
  amenities: IEditableAmenity[];
  onClose: () => void;
  onSave: (amenities: IEditableAmenity[]) => void;
}

export const BulkAmenityEditorModal: React.FC<IProps> = ({
  isOpen,
  amenities: initialAmenities,
  onClose,
  onSave,
}) => {
  const [amenities, setAmenities] = useState<IEditableAmenity[]>([]);

  const DATA_TYPE_OPTIONS: { value: TAmenityDataType; label: string }[] = [
    {
      value: "string",
      label: getLocalizedText("property.extra.custom_amenity.data_type.text"),
    },
    {
      value: "number",
      label: getLocalizedText("property.extra.custom_amenity.data_type.number"),
    },
    {
      value: "boolean",
      label: getLocalizedText(
        "property.extra.custom_amenity.data_type.boolean",
      ),
    },
  ];

  const BOOLEAN_OPTIONS = [
    { id: "true", value: getLocalizedText("system.yes") },
    { id: "false", value: getLocalizedText("system.no") },
  ];
  useEffect(() => {
    if (isOpen) {
      setAmenities(initialAmenities);
    }
  }, [isOpen, initialAmenities]);

  const handleNameChange = (index: number, name: string) => {
    const updated = [...amenities];
    updated[index] = { ...updated[index], name };
    setAmenities(updated);
  };

  const handleDataTypeChange = (index: number, dataType: TAmenityDataType) => {
    const updated = [...amenities];
    // Reset value when data type changes
    let defaultValue: string | number | boolean = "";
    if (dataType === "number") defaultValue = 0;
    if (dataType === "boolean") defaultValue = false;

    updated[index] = { ...updated[index], dataType, value: defaultValue };
    setAmenities(updated);
  };

  const handleValueChange = (
    index: number,
    value: string | number | boolean,
  ) => {
    const updated = [...amenities];
    updated[index] = { ...updated[index], value };
    setAmenities(updated);
  };

  const handleDelete = (index: number) => {
    const updated = amenities.filter((_, i) => i !== index);
    setAmenities(updated);
  };

  const handleAddNew = () => {
    setAmenities([
      ...amenities,
      {
        name: "",
        dataType: "string",
        value: "",
      },
    ]);
  };

  const handleSave = () => {
    onSave(amenities);
  };

  const renderValueInput = (amenity: IEditableAmenity, index: number) => {
    switch (amenity.dataType) {
      case "boolean":
        return (
          <SegmentedControl
            name={`amenity-bool-${index}`}
            selected={String(amenity.value)}
            options={BOOLEAN_OPTIONS}
            onChange={(id: string) => handleValueChange(index, id === "true")}
          />
        );
      case "number":
        return (
          <TextField
            name={`amenity-value-${index}`}
            type="number"
            value={amenity.value === "" ? "" : String(amenity.value)}
            onChange={e => {
              const val = e.target.value;
              handleValueChange(index, val === "" ? "" : parseFloat(val));
            }}
            placeholder="0"
          />
        );
      default:
        return (
          <TextField
            name={`amenity-value-${index}`}
            value={String(amenity.value)}
            onChange={e => handleValueChange(index, e.target.value)}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      heading={getLocalizedText("property.extra.other_amenities.edit")}
      onClose={onClose}
      width="large"
      hasDismiss
      actions={[
        {
          content: getLocalizedText("system.cancel"),
          onClick: onClose,
          appearance: "outline" as const,
        },
        {
          content: getLocalizedText("system.save"),
          onClick: handleSave,
          appearance: "primary" as const,
        },
      ]}
    >
      {amenities.length > 0 ? (
        <DataTable
          headings={[
            getLocalizedText("property.extra.custom_amenity.name"),
            getLocalizedText("property.extra.custom_amenity.data_type"),
            getLocalizedText("property.extra.custom_amenity.value"),
            "",
          ]}
          columnContentTypes={["text", "text", "text", "icon"]}
          rows={amenities.map((amenity, index) => ({
            id: amenity.id || `new-${index}`,
            content: [
              <TextField
                name={`amenity-name-${index}`}
                value={amenity.name}
                onChange={e => handleNameChange(index, e.target.value)}
              />,
              <Select
                id={`amenity-data-type-${index}`}
                label="_"
                labelHidden={true}
                value={amenity.dataType}
                onChange={(selectedOption: {
                  value: TAmenityDataType;
                  label: string;
                }) => handleDataTypeChange(index, selectedOption.value)}
                options={DATA_TYPE_OPTIONS.map(opt => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />,
              renderValueInput(amenity, index),
              <Button
                size="small"
                appearance="danger"
                onClick={() => handleDelete(index)}
                ariaLabel={getLocalizedText("system.delete")}
              >
                <Icon
                  source="bin"
                  size="small"
                  accessibilityLabel={getLocalizedText("system.delete")}
                />
              </Button>,
            ],
          }))}
        />
      ) : (
        <Box mb={4}>
          <EmptyState
            heading={getLocalizedText("property.extra.other_amenities.empty")}
          />
        </Box>
      )}

      <Button
        appearance="outline"
        isFullWidth
        onClick={handleAddNew}
        iconBeforeName="add"
      >
        {getLocalizedText("system.add")}
      </Button>
    </Modal>
  );
};
